/**
 * Humane 429 rate-limit messaging.
 *
 * `T7` (server-driven messaging) does NOT cover ad-hoc client-side errors
 * such as a 429 from an API call the user just made. Without a humane
 * surface the user sees the generic route error card or a console
 * stack — both feel like the product is broken.
 *
 * This module exposes:
 *
 *   - `isRateLimitError(err)` — detector that recognises a thrown
 *     CerberusError / fetch Response / fetch-thrown TypeError as 429.
 *   - `formatRateLimitCopy()` — the canonical Brazilian Portuguese copy.
 *     Kept separate so a translator can update it without touching call
 *     sites. Returns `{ title, subtitle }`.
 *   - `setRateLimitNotifier(fn)` — late-binding hook so top-level catch
 *     handlers can route 429s through
 *     a single toast surface (browser console or a Pheme placement).
 *     The default implementation is `console.warn` so the helper works
 *     without runtime wiring.
 *
 * Wired in production via `src/lib/cerberus/client.ts` and via the
 * fetch instrumentation in Aegis (which sees 4xx/5xx automatically).
 */

const STATUS_RE = /\b(429|too\s*many\s*requests)\b/i

interface RateLimitCopy {
  title: string
  subtitle: string
}

export function formatRateLimitCopy(): RateLimitCopy {
  return {
    title: 'Muitas tentativas seguidas',
    subtitle: 'Espera alguns segundos e tenta de novo — a gente esta aqui pra te ajudar.',
  }
}

/**
 * Returns true if `err` represents a 429-class rate-limit response.
 * Falls back to a string-form heuristic when the thrown object is
 * opaque (e.g. third-party SDK wrappers that don't expose `.status`).
 */
export function isRateLimitError(err: unknown): boolean {
  if (!err) return false
  if (typeof err === 'object' && err !== null) {
    const e = err as { status?: number; statusCode?: number; response?: { status?: number }; message?: string }
    if (e.status === 429) return true
    if (e.statusCode === 429) return true
    if (e.response?.status === 429) return true
    if (typeof e.message === 'string' && STATUS_RE.test(e.message)) return true
  }
  if (typeof err === 'string' && STATUS_RE.test(err)) return true
  return false
}

type Notifier = (copy: RateLimitCopy) => void

let activeNotifier: Notifier = (copy) => {
  if (typeof console !== 'undefined') {
    console.warn(`[rate-limit] ${copy.title} — ${copy.subtitle}`)
  }
}

export function setRateLimitNotifier(fn: Notifier | null): void {
  activeNotifier = fn ?? ((copy) => console.warn(`[rate-limit] ${copy.title} — ${copy.subtitle}`))
}

/**
 * Inspect `err` and, if it is a 429, dispatch the configured notifier.
 * Returns `true` when a notification was emitted so the caller can
 * suppress duplicate generic-error UI.
 */
export function maybeNotifyRateLimit(err: unknown): boolean {
  if (!isRateLimitError(err)) return false
  try {
    activeNotifier(formatRateLimitCopy())
  } catch {
    // notifier crash must never propagate
  }
  return true
}
